import { message } from 'antd';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { memo, useCallback, useMemo, useState } from 'react';

import CommonSimpleModal from '@/components/Common/CommonSimpleModal';
import DownloadStoriesBottomContent from '@/components/LandingPage/DownloadStoriesBottomContent';
import { CommonBackContent } from '@/components/Layouts/CommonBackContent';
import { getThreadsPostDetail } from '@/lib/service/threads';
import { EventName } from '@/shared/event-types';
import { ga_logEvent } from '@/shared/ga';
import { HeroData } from '@/types/home';
import { ThreadsPostDetailModelV1, ThreadsPostItemModelV1 } from '@/types/threads';
import ThreadsDownloaderSearchResult from '../ThreadsSearchResult';
import ThreadsPostRecommend from '../ThreadsPostRecommend';
import TopSearchContent from '@/components/LandingPage/TopSearch';
import { defaultSearchStr, getLastUrlParam, makeThreadsPostUrl, validateThreadsPostUrl } from '../shared';

interface ThreadsDownloaderSearchProps {
  children?: React.ReactNode;
}

const ThreadsDownloaderSearch: React.FC<ThreadsDownloaderSearchProps> = ({ children }) => {
  const { t } = useTranslation();
  const router = useRouter();

  const [loading, setLoading] = useState(false);
  const [inputVisible, setInputVisible] = useState(false);
  const [searchStr, setSearchStr] = useState(() => {
    if (typeof window !== 'undefined' && location.hostname === 'localhost') {
      return defaultSearchStr;
    }
    return '';
  });
  const [searchResult, setSearchResult] = useState<ThreadsPostDetailModelV1>();
  const [alertInfo, setAlertInfo] = useState<any>(null);
  const [searchId, setSearchId] = useState(0); // 用来作为key值，避免整个组件刷新

  // 使用useMemo缓存topInfo
  const topInfo = useMemo<HeroData>(
    () => ({
      title: t('Threads Downloader'),
      subTitle: t('Download Threads Videos and GIFs Online for Free'),
      seoTitle: `${t('Threads Downloader')} - ${t('Download Threads Videos and GIFs Online for Free')}`,
      desc: t(
        `Whether you're backing up your posts, sharing content elsewhere, or watching videos offline, Dolphin Radar makes it easy to download Threads videos, GIFs, or images in high quality. Just paste a link, click download, and you're done — completely free.`
      ),
      tags: [
        t(`<p>Trusted by <span style='color:#725CFF'>xxx</span> Users</p>`).replace('xxx', '50,000+'),
        t(`<p><span style='color:#725CFF'>xxx</span> Free</p>`).replace('xxx', '100%'),
        t('HD Quality'),
        t('No Watermarks'),
      ],
    }),
    []
  );

  // 使用useCallback优化函数
  const handleClickPost = useCallback((username: string, post: ThreadsPostItemModelV1) => {
    window.scrollTo({ top: 600, behavior: 'smooth' });

    setSearchStr(makeThreadsPostUrl(username, post));

    // 优化状态更新，避免重新渲染整个组件
    setSearchResult((prev) => {
      if (!prev) return undefined;
      return {
        ...prev,
        post_detail: post,
      };
    });
  }, []);

  // 优化搜索逻辑
  const performSearch = useCallback((str: string) => {
    if (!validateThreadsPostUrl(str)) {
      setAlertInfo({
        title: t('Invalid Link'),
        desc: t("Please enter a valid Threads post link. Profile or homepage links won't work."),
        btnTitle: t('TRYAGAIN'),
      });
      return;
    }

    try {
      const postCode = getLastUrlParam(str);
      if (!postCode) {
        setAlertInfo({
          title: t('Invalid Link'),
          desc: t("Please enter a valid Threads post link. Profile or homepage links won't work."),
          btnTitle: t('TRYAGAIN'),
        });
        return;
      }

      setLoading(true);
      getThreadsPostDetail(postCode, { isPremium: false, bizFuncName: 'threads' })
        .then((res) => {
          if (res.code === 0) {
            setSearchResult(res.data);
            // 生成新的searchId，用于ResultComponent的key
            setSearchId((prev) => prev + 1);
          } else {
            if (res.code === 24001) {
              setAlertInfo({
                title: t('Post Not Found'),
                desc: t("We couldn't find the Threads post. It may have been deleted or the link is incorrect."),
                btnTitle: t('TRYAGAIN'),
              });
            } else {
              message.error(res.message);
            }
          }
        })
        .catch((err) => {
          message.error(err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      message.error('Invalid URL format');
      setLoading(false);
    }
  }, []);

  const onValueChanged = useCallback((val: string) => {
    setSearchStr(val);
  }, []);

  const handleSearch = useCallback(
    (str: string) => {
      performSearch(str);
      ga_logEvent(EventName.threads_downloader_s_n);
    },
    [performSearch]
  );

  // 使用useMemo缓存搜索结果组件，避免不必要的重渲染
  const resultComponent = useMemo(() => {
    if (!searchResult) return null;
    return <ThreadsDownloaderSearchResult key={`search-result-${searchId}`} data={searchResult} />;
  }, [searchResult, searchId]);

  // 使用useMemo缓存推荐帖子组件
  const recommendComponent = useMemo(() => {
    if (!searchResult) return null;
    return (
      <ThreadsPostRecommend
        mediaName={searchResult.user?.username}
        existCode={searchResult.post_detail?.code}
        onClickPost={handleClickPost}
      />
    );
  }, [searchResult, handleClickPost]);

  return (
    <>
      <CommonBackContent>
        <TopSearchContent
          topInfo={topInfo}
          loading={loading}
          searchVal={searchStr}
          inputClassName="w-[656px]"
          inputIcon={
            <Image src={'/images/threads-downloader/link-outline-icon.webp'} alt="link" width={24} height={24} />
          }
          inputPlaceholder={t('Enter the URL of the Threads post you want to download')}
          showSearchBtnWithFullWidth={true}
          onSearchClick={handleSearch}
          onValueChanged={onValueChanged}
          onInputVisibleChanged={setInputVisible}
        />

        {!searchResult && (
          <div className="mt-9 mb-20 max-md:mb-9 text-center text-base text-[#0c0c0c99]">
            {t('Paste a Threads post URL above to load its videos and show download options here')}
          </div>
        )}

        {resultComponent}
        {recommendComponent}

        {children}
      </CommonBackContent>

      <DownloadStoriesBottomContent
        bottomInfo={{
          title: t('Download Threads Videos, GIFs, and Images Instantly'),
          desc: t(`Try Dolphin Radar — it's free, fast, and lets you download Threads media in just one click.`),
        }}
        inputIcon={
          <Image src={'/images/threads-downloader/link-outline-icon.webp'} alt="link" width={24} height={24} />
        }
        placeholder={t('Enter the URL of the Threads post you want to download')}
        loading={loading}
        searchVal={searchStr}
        inputVisible={inputVisible}
        onValueChanged={onValueChanged}
        onSearchClick={(val) => {
          window.scrollTo({ top: 450 });
          handleSearch(val);
        }}
      />

      <CommonSimpleModal
        open={alertInfo !== null}
        title={alertInfo?.title}
        desc={alertInfo?.desc}
        btnTitle={alertInfo?.btnTitle}
        closeAction={(isOk) => {
          setAlertInfo(null);
        }}
      />
    </>
  );
};

export default memo(ThreadsDownloaderSearch);
